import { useMemo } from 'react';

import { FeatureMode } from '@/modules/common/components/templates/feature-details/types/types';
import { RateSource } from '@/modules/common/types/rate-source';
import { ProjectBillingModel } from '@/modules/projects/types/project';

import { BillingModelOption, RateSourceOption } from '../../types';

interface UseBillingLogicProps {
  mode: FeatureMode;
  originalBillingModel?: ProjectBillingModel;
  currentBillingModel: ProjectBillingModel;
  currentRateSource: RateSource;
  t: (key: string) => string;
}

interface UseBillingLogicReturn {
  billingModelOptions: BillingModelOption[];
  rateSourceOptions: RateSourceOption[];
  teamMemberRateSourceOptions: RateSourceOption[];
  shouldShowBillingModelWarning: boolean;
  showRateSource: boolean;
  showFlatRate: boolean;
  showProjectBudget: boolean;
  shouldShowRateSourceAlert: boolean;
}

export function useBillingLogic({
  mode,
  originalBillingModel,
  currentBillingModel,
  currentRateSource,
  t,
}: UseBillingLogicProps): UseBillingLogicReturn {
  /**
   * Determines available billing model options based on current project state and mode.
   * Implements business rules for billing model transitions to prevent invalid changes.
   *
   * For projects without a billing model from backend (undefined), they are treated as T&M
   * and follow standard T&M transition rules (no transitions to FixedPrice or NonBillable).
   */
  const billingModelOptions = useMemo((): BillingModelOption[] => {
    const baseOptions: BillingModelOption[] = [
      { value: ProjectBillingModel.TM, label: t('filters.timeMaterial') },
      { value: ProjectBillingModel.FixedPrice, label: t('filters.fixedPrice') },
      { value: ProjectBillingModel.NonBillable, label: t('filters.nonBillable') },
    ];

    if (mode === FeatureMode.Create) {
      return baseOptions;
    }

    // Business logic for billing model transitions in edit mode
    const disallowedTransitions: Record<ProjectBillingModel, ProjectBillingModel[]> = {
      [ProjectBillingModel.TM]: [ProjectBillingModel.FixedPrice, ProjectBillingModel.NonBillable],
      [ProjectBillingModel.FixedPrice]: [ProjectBillingModel.TM, ProjectBillingModel.NonBillable],
      [ProjectBillingModel.NonBillable]: [], // Can transition to any
    };

    // If originalBillingModel is undefined (no billing model from backend), treat it as T&M
    const effectiveOriginalBillingModel = originalBillingModel ?? ProjectBillingModel.TM;
    const disallowedOptions = disallowedTransitions[effectiveOriginalBillingModel] || [];

    return baseOptions.map(option => {
      const isItemDisabled = disallowedOptions.includes(option.value);
      return {
        ...option,
        isItemDisabled,
        // Explain why the option is locked when the original model is T&M or Fixed Price
        tooltip: isItemDisabled ? t('details.tabs.billing.billingModelLockedTooltip') : undefined,
      };
    });
  }, [mode, originalBillingModel, t]);

  /**
   * Determines available rate source options
   *
   * NOTE: SeniorityBasedRate and RoleBasedRate are temporarily hidden (post-MVP features)
   * TODO: Uncomment when seniority-based and role-based rates are ready
   */
  const rateSourceOptions = useMemo(
    (): RateSourceOption[] => [
      { value: RateSource.FlatRate, label: t('details.tabs.billing.rateSource.flatRate') },
      {
        value: RateSource.UserExternalRate,
        label: t('details.tabs.billing.rateSource.userExternalRate'),
      },
      // TODO: Post-MVP - Uncomment when ready
      // {
      //   value: RateSource.SeniorityBasedRate,
      //   label: t('details.tabs.billing.rateSource.seniorityBasedRate'),
      // },
      // {
      //   value: RateSource.RoleBasedRate,
      //   label: t('details.tabs.billing.rateSource.roleBasedRate'),
      // },
      {
        value: RateSource.GlobalDefaultRate,
        label: t('details.tabs.billing.rateSource.globalDefaultRate'),
      },
      {
        value: RateSource.ProjectRateCard,
        label: t('details.tabs.billing.rateSource.projectRateCard'),
      },
    ],
    [t]
  );

  /**
   * Rate source options available for team members when project rate source is ProjectRateCard
   *
   * NOTE: SeniorityBasedRate and RoleBasedRate are temporarily hidden (post-MVP features)
   * TODO: Uncomment when seniority-based and role-based rates are ready
   */
  const teamMemberRateSourceOptions = useMemo(
    (): RateSourceOption[] => [
      {
        value: RateSource.UserExternalRate,
        label: t('details.tabs.billing.rateSource.userExternalRate'),
      },
      // TODO: Post-MVP - Uncomment when ready
      // {
      //   value: RateSource.SeniorityBasedRate,
      //   label: t('details.tabs.billing.rateSource.seniorityBasedRate'),
      // },
      // {
      //   value: RateSource.RoleBasedRate,
      //   label: t('details.tabs.billing.rateSource.roleBasedRate'),
      // },
      {
        value: RateSource.GlobalDefaultRate,
        label: t('details.tabs.billing.rateSource.globalDefaultRate'),
      },
      { value: RateSource.Custom, label: t('details.tabs.billing.customRate') },
    ],
    [t]
  );

  /**
   * Determines if billing model change warning should be displayed
   */
  const shouldShowBillingModelWarning = useMemo((): boolean => {
    // If originalBillingModel is undefined (no billing model from backend), treat it as T&M
    const effectiveOriginalBillingModel = originalBillingModel ?? ProjectBillingModel.TM;
    return (
      effectiveOriginalBillingModel === ProjectBillingModel.NonBillable &&
      currentBillingModel !== ProjectBillingModel.NonBillable &&
      mode !== FeatureMode.Create
    );
  }, [originalBillingModel, currentBillingModel, mode]);

  // Determine which fields to show based on billing model and rate source
  const showRateSource = currentBillingModel !== ProjectBillingModel.FixedPrice;
  const showFlatRate =
    currentRateSource === RateSource.FlatRate &&
    currentBillingModel !== ProjectBillingModel.FixedPrice;
  const showProjectBudget =
    currentBillingModel === ProjectBillingModel.TM ||
    currentBillingModel === ProjectBillingModel.FixedPrice;

  // Rate source options that show alert
  // NOTE: SeniorityBasedRate and RoleBasedRate temporarily removed (post-MVP features)
  // TODO: Add back when seniority-based and role-based rates are ready
  const rateSourceOptionsWithAlert = [
    RateSource.UserExternalRate,
    // RateSource.SeniorityBasedRate, // TODO: Post-MVP - Uncomment when ready
    // RateSource.RoleBasedRate, // TODO: Post-MVP - Uncomment when ready
    RateSource.GlobalDefaultRate,
  ];

  const shouldShowRateSourceAlert =
    rateSourceOptionsWithAlert.includes(currentRateSource) && showRateSource;

  return {
    billingModelOptions,
    rateSourceOptions,
    teamMemberRateSourceOptions,
    shouldShowBillingModelWarning,
    showRateSource,
    showFlatRate,
    showProjectBudget,
    shouldShowRateSourceAlert,
  };
}
